














































































/* General */
import Vue from 'vue'
import { get } from 'vuex-pathify'
import DefaultLayout from '@/layouts/default-layout.vue'

/* Mixins */

/* Components */
import MainContainer from '@/layouts/main-container.vue'
import DashboardWomp from './components/dashboard-womp.vue'
import DashboardPinboard from './components/dashboard-pinboard.vue'
import DashboardMailboxes from './views/dashboard-mailboxes.vue'
import DashboardProjects from './views/dashboard-projects.vue'
import DashboardHeader from './views/dashboard-header.vue'
import DashboardArchived from './views/dashboard-archived.vue'

/* Helpers */
import LocalStore from '@/helpers/localstore'

/* Data types */
import {
  IDashboardOutputFavorite,
  IProjectDailyActivityLogsDailyActivityLog,
  IProjectsOutputProjectForHome,
} from '@/api/types/response.types'

/* API */
import DashboardAPI from '@/api/modules/dashboard.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'

export default Vue.extend({
  name: 'DashboardScreen',

  metaInfo(): any {
    return {
      title: `${this.company.name}`,
    }
  },

  components: {
    DefaultLayout,
    MainContainer,
    DashboardWomp,
    DashboardPinboard,
    DashboardHeader,
    DashboardMailboxes,
    DashboardProjects,
    DashboardArchived,
  },

  data() {
    return {
      desktopViewType: '',
      searchQuery: '',
      favorites: [] as IDashboardOutputFavorite[],
      projectData: [] as IProjectsOutputProjectForHome[],
      mailboxData: [] as IProjectsOutputProjectForHome[],
      projectDailyActivityLogs: [] as IProjectDailyActivityLogsDailyActivityLog[],
      mailboxDailyActivityLogs: [] as IProjectDailyActivityLogsDailyActivityLog[],
      archivedMailboxCount: 0,
      archivedProjectCount: 0,
      mailboxLoading: true,
      projectLoading: true,
      showArchived: false,
      showDashboardProjectsFirst: false,

      /* Polling data */
      lastAccessEpochTime: null,
      poller: null,
      pollIntervalInMS: this.$store.get('app/autoRefreshDataPingInMS'),
    }
  },

  computed: {
    company: get('profile/currentCompany'),
    archiveEnabled(): boolean {
      return (
        !this.$screen.isMobile(this.$mq) &&
        this.$permissions.isAdminOrOwner &&
        (this.archivedMailboxCount > 0 || this.archivedProjectCount > 0)
      )
    },
    archiveLabel(): string {
      let result = 'View archived'
      if (this.archivedMailboxCount > 0) {
        result += ' mailboxes (' + this.archivedMailboxCount + ')'
        if (this.archivedProjectCount > 0) {
          result += ' & '
        }
      }
      if (this.archivedProjectCount > 0) {
        result += ' projects (' + this.archivedProjectCount + ')'
      }

      return result
    },
    /**
     * Filter mailbox list based on entered string
     */
    mailboxesToDisplay(): IProjectsOutputProjectForHome[] {
      if (this.searchQuery !== '') {
        return this.mailboxData.filter((item: any) => {
          return item.name
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
        })
      }
      return this.mailboxData
    },

    /**
     * Filter projects list based on entered string
     */
    projectsToDisplay(): IProjectsOutputProjectForHome[] {
      if (this.searchQuery !== '') {
        return this.projectData.filter((item: any) => {
          return item.name
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
        })
      }
      return this.projectData
    },

    isSearching(): boolean {
      return this.searchQuery !== ''
    },

    isLoaded(): boolean {
      return !this.projectLoading && !this.mailboxLoading
    },

    onlyProjectsExist(): boolean {
      return (
        !this.mailboxLoading &&
        !this.projectLoading &&
        this.mailboxData.length === 0 &&
        this.projectData.length > 0
      )
    },

    onlyMailboxesExist(): boolean {
      return (
        !this.mailboxLoading &&
        !this.projectLoading &&
        this.projectData.length === 0 &&
        this.mailboxData.length > 0
      )
    },

    hasProjects(): boolean {
      return !this.projectLoading && this.projectData.length > 0
    },

    hasMailboxes(): boolean {
      return !this.mailboxLoading && this.mailboxData.length > 0
    },

    showMailboxes(): boolean {
      return this.$permissions.isNotGuest
    },

    viewType(): string {
      if (this.$screen.isTabletOrMobile(this.$mq)) {
        return 'table'
      }
      return this.desktopViewType
    },
  },

  beforeDestroy() {
    this.endPolling()
  },

  mounted() {
    this.updateDesktopViewType()
  },

  async created() {
    try {
      this.showDashboardProjectsFirst = LocalStore.dashboardProjectsFirst
      await this.getDashboardEntityInfo()
      this.startPolling()
    } catch (err) {
      this.$error.emit(err)
    }
  },

  methods: {
    updateDesktopViewType() {
      const cachedViewType = LocalStore.dashboardViewType
      if (['grid', 'table'].includes(cachedViewType)) {
        this.desktopViewType = cachedViewType
      } else {
        this.desktopViewType = 'grid'
      }
    },
    toggleEntityOrdering() {
      this.showDashboardProjectsFirst = !this.showDashboardProjectsFirst
      LocalStore.dashboardProjectsFirst = this.showDashboardProjectsFirst
    },
    startPolling() {
      // Ensure we're clearing old polling, if necessary
      this.endPolling()
      this.poller = setInterval(async () => {
        try {
          await this.autoRefreshDataIfStale()
        } catch {
          this.endPolling()
        }
      }, this.pollIntervalInMS)
    },
    endPolling() {
      clearInterval(this.poller)
    },
    async autoRefreshDataIfStale() {
      const lastUpdatedEpochTime = await DashboardAPI.get_MostRecentLastUpdatedProjectEpochTime()
      if (
        lastUpdatedEpochTime.epochTime &&
        this.lastAccessEpochTime < lastUpdatedEpochTime.epochTime
      ) {
        await this.getDashboardEntityInfo(false)
        this.lastAccessEpochTime = lastUpdatedEpochTime.epochTime
      }
    },
    async getDashboardEntityInfo(showResultsLoading: boolean = true) {
      this.mailboxLoading = showResultsLoading
      this.projectLoading = showResultsLoading

      const response = await DashboardAPI.get_ProjectInfo()
      this.lastAccessEpochTime = response.accessEpochTime
      this.mailboxData = response.listMailboxes
      this.projectData = response.listInternalProjects
      this.archivedMailboxCount = response.archivedMailboxCount
      this.archivedProjectCount = response.archivedInternalProjectCount

      this.mailboxLoading = false
      this.projectLoading = false

      Promise.all([
        this.getMailboxDailyActivityLogs(),
        this.getProjectDailyActivityLogs(),
      ])
    },

    async getMailboxDailyActivityLogs() {
      if (!this.hasMailboxes) {
        return
      }
      /* Return for mobile as it doesn't use activity charts */
      if (this.$screen.isMobile(this.$mq)) {
        return
      }
      try {
        const mailboxDailyActivityLogsWrapper = await MailboxesAPI.get_DailyActivityLogs()
        this.mailboxDailyActivityLogs =
          mailboxDailyActivityLogsWrapper.listDailyActivityLogs
      } catch {
        // Do nothing, in case it's a timeout from slow DB...
      }
    },

    async getProjectDailyActivityLogs() {
      if (!this.hasProjects) {
        return
      }
      /* Return for mobile as it doesn't use activity charts */
      if (this.$screen.isMobile(this.$mq)) {
        return
      }
      try {
        const projectDailyActivityLogsWrapper = await InternalProjectsAPI.get_DailyActivityLogs()
        this.projectDailyActivityLogs =
          projectDailyActivityLogsWrapper.listDailyActivityLogs
      } catch {
        // Do nothing, in case it's a timeout from slow DB...
      }
    },

    async reloadComponentData() {
      await Promise.all([
        // @ts-ignore
        this.$refs.womp.getWompStats(),
        // @ts-ignore
        this.$refs.pinboard.getPinnedItems(),
      ])
    },

    async onMailboxRestored() {
      this.archivedMailboxCount--
      this.mailboxLoading = true
      this.mailboxData = await MailboxesAPI.get_Index()
      this.mailboxLoading = false
      this.getMailboxDailyActivityLogs()
      await this.reloadComponentData()
    },

    async onProjectRestored() {
      this.archivedProjectCount--
      this.projectLoading = true
      this.projectData = await InternalProjectsAPI.get_Index()
      this.projectLoading = false
      this.getProjectDailyActivityLogs()
      await this.reloadComponentData()
    },

    async removeMailbox(removedMailbox: IProjectsOutputProjectForHome) {
      this.mailboxData = this.mailboxData.filter(
        m => m.id !== removedMailbox.id,
      )
      this.archivedMailboxCount++
      await this.reloadComponentData()
    },

    async removeProject(removedProject: IProjectsOutputProjectForHome) {
      this.projectData = this.projectData.filter(
        m => m.id !== removedProject.id,
      )
      this.archivedProjectCount++
      await this.reloadComponentData()
    },

    changeView(value: string) {
      LocalStore.dashboardViewType = value
      this.updateDesktopViewType()
    },
  },
})
